/**
 * Keeps the product grid on whole rows while more products can still be loaded.
 *
 * A page of products rarely divides evenly by the column count, so the grid ends in a short row
 * with a gap beside it and the Load More button under that. Those trailing cards are hidden until
 * the next page arrives, at which point they are revealed together with it — a card is deferred by
 * at most one click and never skipped. Once the last page is loaded nothing is trimmed, so every
 * product is always reachable.
 *
 * Ported from goat-shopify-redesign #193, which computes the trim in Liquid because its grid is a
 * fixed 3 or 4 columns. Ours is `repeat(auto-fill, minmax(...))`, so the column count depends on
 * the rendered width and the shopper's grid-view toggle and is only knowable at runtime — hence
 * measuring the resolved `grid-template-columns` here rather than calculating it server-side.
 */

const TRIMMED_CLASS = 'product-grid__item--row-trim';
const GRID_SELECTOR = '.product-grid[ref="grid"]';
const CELL_SELECTOR = ':scope > [ref="cards[]"]';

/** @type {WeakSet<HTMLElement>} */
const observed = new WeakSet();

/**
 * Whether more products can still be loaded into this grid.
 *
 * Keyed off the Load More button because that is the element whose visibility already tracks
 * "another page exists" — `paginated-list.js` hides its container when the last page lands.
 * Grids in infinite-scroll mode have no button and are deliberately left untrimmed: they load the
 * next page on their own, so a short row is momentary and hiding cards would fight the scroll.
 *
 * @param {HTMLElement} grid
 * @returns {boolean}
 */
function hasMorePages(grid) {
  const list = grid.closest('results-list, blog-posts-list') ?? grid.parentElement;
  const button = list?.querySelector('[ref="loadMoreButton"]');
  if (!button) return false;

  const container = button.closest('.load-more-container');
  return !(container instanceof HTMLElement) || !container.hidden;
}

/**
 * Reads the column count from the resolved grid template. `auto-fill` tracks are sized from the
 * container width, so hiding cells does not change the count and the measurement stays stable
 * across a trim.
 *
 * @param {HTMLElement} grid
 * @returns {number}
 */
function columnCount(grid) {
  const template = getComputedStyle(grid).gridTemplateColumns;
  if (!template || template === 'none') return 0;
  return template.split(' ').filter(Boolean).length;
}

/**
 * @param {HTMLElement} grid
 */
function applyRowTrim(grid) {
  const cells = /** @type {HTMLElement[]} */ (Array.from(grid.querySelectorAll(CELL_SELECTOR)));

  // Always measure the untrimmed grid, otherwise each pass would trim what the last pass left.
  for (const cell of cells) cell.classList.remove(TRIMMED_CLASS);

  if (!cells.length || !hasMorePages(grid)) return;

  const columns = columnCount(grid);
  if (columns < 2) return;

  const remainder = cells.length % columns;
  if (!remainder) return;

  for (let i = 0; i < remainder; i++) {
    cells[cells.length - 1 - i]?.classList.add(TRIMMED_CLASS);
  }
}

/** @type {WeakMap<HTMLElement, ReturnType<typeof setTimeout>>} */
const scheduled = new WeakMap();

/**
 * Coalesces the bursts of notifications an append produces into a single pass.
 *
 * Deliberately a timeout rather than `requestAnimationFrame`: rAF does not run while the tab is
 * hidden, so a collection opened in a background tab would render its ragged row and keep it until
 * the shopper focused the tab. Nothing here needs frame timing — the measurement is a style read.
 *
 * @param {HTMLElement} grid
 */
function schedule(grid) {
  if (scheduled.has(grid)) return;

  scheduled.set(
    grid,
    setTimeout(() => {
      scheduled.delete(grid);
      applyRowTrim(grid);
    }, 0)
  );
}

/**
 * @param {HTMLElement} grid
 */
function observe(grid) {
  if (observed.has(grid)) return;
  observed.add(grid);

  // Cards appended by Load More, and the grid-view toggle, which changes the column count without
  // changing the grid's width. Attributes are watched on the grid itself only — watching the
  // subtree would pick up this module's own class changes and loop.
  new MutationObserver(() => schedule(grid)).observe(grid, {
    childList: true,
    attributes: true,
    attributeFilter: ['product-grid-view', 'data-last-page'],
  });

  // Width changes that add or remove an auto-fill column.
  new ResizeObserver(() => schedule(grid)).observe(grid);

  // The button's container is hidden once the last page arrives, which is what ends the trimming.
  const button = grid.closest('results-list, blog-posts-list')?.querySelector('[ref="loadMoreButton"]');
  const container = button?.closest('.load-more-container');
  if (container) {
    new MutationObserver(() => schedule(grid)).observe(container, {
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }

  schedule(grid);
}

function init() {
  for (const grid of document.querySelectorAll(GRID_SELECTOR)) {
    if (grid instanceof HTMLElement) observe(grid);
  }
}

init();

// A filter or sort change re-renders the section, replacing the grid element, so the new one has
// to be picked up. The events fire after the morph has landed. Names come from
// `StandardEvents.collectionUpdate` / `.searchUpdate` in @shopify/events, inlined because this
// module is loaded directly rather than through the import map.
document.addEventListener('shopify:collection:update', init);
document.addEventListener('shopify:search:update', init);

// Theme editor reloads a section in place.
document.addEventListener('shopify:section:load', init);
